import { Component, lazy, Suspense } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { getAuthSession } from '@/lib/api';
import { LandingPage } from '@/components/landing/LandingPage';
import { resolveRootView } from '@/components/landing/root-view';
import { Layout } from '@/components/layout/Layout';
import { AuthGate } from '@/components/auth/AuthGate';
import { ProjectProvider } from '@/contexts/ProjectContext';
import { queryClient } from '@/lib/query-client';
import { BootSplash } from '@/components/boot/BootSplash';
import { useBootState } from '@/hooks/useBootState';
import { DashboardPage } from '@/routes/DashboardPage';
import { ChatPage } from '@/routes/ChatPage';
import { WorkflowsPage } from '@/routes/WorkflowsPage';
import { WorkflowExecutionPage } from '@/routes/WorkflowExecutionPage';
import { WorkflowBuilderPage } from '@/routes/WorkflowBuilderPage';
import { SettingsPage } from '@/routes/SettingsPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { OutcomesPage } from '@/routes/OutcomesPage';
import { OutcomeDetailPage } from '@/routes/OutcomeDetailPage';
import { ProjectsPage } from '@/routes/ProjectsPage';
import { GBrainPage } from '@/routes/GBrainPage';
import { DeliveryPage } from '@/routes/DeliveryPage';
import { IntelligencePage } from '@/routes/IntelligencePage';
import { LocalEnrollmentPage } from '@/routes/LocalEnrollmentPage';
import { AaronLiveDeploymentPage } from '@/routes/AaronLiveDeploymentPage';
import { WorkbenchPage } from '@/routes/WorkbenchPage';

// Suspense fallback for 3D explorers
function ExplorerSuspenseFallback(): React.ReactElement {
  return (
    <div className="flex flex-1 items-center justify-center text-sm text-text-secondary">
      Loading 3D Explorer...
    </div>
  );
}

// Lazy-load 3D explorers to reduce main bundle size
// eslint-disable-next-line @typescript-eslint/naming-convention
const GBrainExplorer3D = lazy(() =>
  import('@/routes/GBrainExplorer3D').then(m => ({ default: m.GBrainExplorer3D }))
);
// eslint-disable-next-line @typescript-eslint/naming-convention
const IntelExplorer3D = lazy(() =>
  import('@/routes/IntelExplorer3D').then(m => ({ default: m.IntelExplorer3D }))
);

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught rendering error', {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-background p-8">
          <div className="max-w-md text-center">
            <h1 className="mb-2 text-xl font-semibold text-text-primary">Something went wrong</h1>
            <p className="mb-4 text-sm text-text-secondary">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
            <button
              onClick={(): void => {
                window.location.reload();
              }}
              className="rounded-md bg-surface-elevated px-4 py-2 text-sm text-text-primary hover:bg-surface-hover"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * BootGate — renders the splash until the platform reports phase=ready
 * or phase=degraded. Blocks the router mount so no UI code runs against
 * a half-initialized backend. `failed` keeps the splash up indefinitely
 * so the operator sees the actionable failure detail; fetch errors (e.g.
 * server unreachable) surface in the splash too.
 */
function BootGate({ children }: { children: ReactNode }): React.ReactElement {
  const { data, isError, error } = useBootState();
  const phase = data?.phase;
  const appReady = phase === 'ready' || phase === 'degraded';
  if (!appReady) {
    return <BootSplash state={data} isError={isError} error={error} />;
  }
  return <>{children}</>;
}

/**
 * AuthedApp — the layout route that guards every operator surface.
 *
 * AuthGate used to wrap the whole tree ABOVE BrowserRouter, which meant no route
 * could render without a session — including `/`. Moving the router above it and
 * demoting AuthGate to a layout route is what lets `/` be public while every
 * other path keeps exactly the same gate it had before.
 *
 * ProjectProvider stays INSIDE the gate: it reads operator-scoped data and has
 * no meaning for a signed-out visitor.
 */
function AuthedApp(): React.ReactElement {
  return (
    <AuthGate>
      <ProjectProvider>
        <Layout />
      </ProjectProvider>
    </AuthGate>
  );
}

/**
 * RootRoute — `/` is public for visitors and a doorway for operators.
 *
 * Signed in  -> straight to /chat, which is what `/` did unconditionally before.
 * Signed out -> the public landing page.
 *
 * Renders nothing while the session resolves, deliberately: showing the landing
 * page first would flash marketing copy at an operator on every single visit,
 * and the session query is one local request against an already-warm SPA.
 *
 * A failed query falls through to the landing page rather than an error screen.
 * `/` is a public surface; a stranger should not be shown an internal failure,
 * and the operator will meet the real error at /chat where AuthGate reports it.
 *
 * Note for self-hosted Docker: `GET /api/auth/session` AUTO-PROVISIONS a session
 * when MAESTRO_DOCKER=true (see packages/server/src/routes/api/auth.ts). Such an
 * instance therefore always resolves as authenticated and goes straight to the
 * app — which is the correct behaviour for a single-operator deployment, and is
 * identical to what it did before this change.
 */
function RootRoute(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
    retry: 2,
  });
  switch (resolveRootView({ isLoading, data })) {
    case 'loading':
      return <></>;
    case 'app':
      return <Navigate to="/chat" replace />;
    case 'landing':
      return <LandingPage />;
  }
}

export function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BootGate>
          <BrowserRouter
            basename={
              ((import.meta.env.VITE_BASE_PATH as string | undefined) ?? '/').replace(/\/$/, '') ||
              undefined
            }
          >
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route element={<AuthedApp />}>
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/*" element={<ChatPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/workbench" element={<WorkbenchPage />} />
                <Route path="/local-enrollment" element={<LocalEnrollmentPage />} />
                <Route
                  path="/operations/aaron-live-deployment"
                  element={<AaronLiveDeploymentPage />}
                />
                <Route path="/workflows" element={<WorkflowsPage />} />
                <Route path="/workflows/builder" element={<WorkflowBuilderPage />} />
                <Route path="/workflows/runs/:runId" element={<WorkflowExecutionPage />} />
                <Route path="/workflows/runs" element={<Navigate to="/workflows" replace />} />
                <Route path="/outcomes" element={<OutcomesPage />} />
                <Route path="/outcomes/:outcomeId" element={<OutcomeDetailPage />} />
                <Route path="/delivery" element={<DeliveryPage />} />
                <Route path="/g-brain" element={<GBrainPage />} />
                <Route
                  path="/g-brain/explorer"
                  element={
                    <Suspense fallback={<ExplorerSuspenseFallback />}>
                      <GBrainExplorer3D />
                    </Suspense>
                  }
                />
                <Route path="/intelligence" element={<IntelligencePage />} />
                <Route
                  path="/intelligence/explorer"
                  element={
                    <Suspense fallback={<ExplorerSuspenseFallback />}>
                      <IntelExplorer3D />
                    </Suspense>
                  }
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </BootGate>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
